// src/components/evidence/EvidenceBank.tsx
// Track and display evidence of transformation

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gem, ChevronLeft, Plus, Star, Filter, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSoulFile } from '../../hooks/useSoulFile';
import { supabase } from '../../lib/supabase/browserClient';
import { useAuth } from '../../hooks/useAuth';
import { ToolHeader } from '../ui/ToolHeader';

type EvidenceCategory = 'identity' | 'manifestation' | 'synchronicity' | 'feeling' | 'pattern-break';

interface Evidence {
  id: string;
  category: EvidenceCategory;
  description: string;
  is_highlight: boolean;
  created_at: string;
  manifestation_id?: string;
}

const CATEGORY_CONFIG: Record<EvidenceCategory, { label: string; emoji: string; color: string; description: string }> = {
  identity: { 
    label: 'Identity Shift', 
    emoji: '🦋', 
    color: 'from-purple-400 to-pink-400',
    description: 'Moments when you showed up as your future self'
  },
  manifestation: { 
    label: 'Manifestation', 
    emoji: '✨', 
    color: 'from-amber-400 to-orange-400',
    description: 'Things that showed up in your reality'
  },
  synchronicity: { 
    label: 'Synchronicity', 
    emoji: '🔮', 
    color: 'from-indigo-400 to-purple-400',
    description: 'Meaningful coincidences and signs'
  },
  feeling: { 
    label: 'Feeling Shift', 
    emoji: '💜', 
    color: 'from-pink-400 to-rose-400',
    description: 'Changes in how you feel'
  },
  'pattern-break': { 
    label: 'Pattern Break', 
    emoji: '⚡', 
    color: 'from-cyan-400 to-blue-400',
    description: 'Times you responded differently than before'
  },
};

interface EvidenceBankProps {
  onClose?: () => void;
  embedded?: boolean;
}

export function EvidenceBank({ onClose, embedded = false }: EvidenceBankProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { soulFile, addEvidence } = useSoulFile();
  
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<EvidenceCategory | 'all'>('all');
  
  // Add form state
  const [newCategory, setNewCategory] = useState<EvidenceCategory>('identity');
  const [newDescription, setNewDescription] = useState('');
  const [newIsHighlight, setNewIsHighlight] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadEvidence();
    }
  }, [user?.id]);

  const loadEvidence = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    
    const { data, error } = await supabase
      .from('evidence')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setEvidence(data);
    }
    
    setLoading(false);
  };

  const handleAddEvidence = async () => {
    if (!user?.id || !newDescription.trim()) return;
    
    setSaving(true);
    
    try {
      const { error } = await supabase.from('evidence').insert({
        user_id: user.id,
        category: newCategory,
        description: newDescription.trim(),
        is_highlight: newIsHighlight,
      });

      if (!error) {
        setShowAddModal(false);
        setNewDescription('');
        setNewIsHighlight(false);
        loadEvidence();

        // Log activity
        await supabase.from('activity_log').insert({
          user_id: user.id,
          activity_type: 'evidence-logged',
          activity_date: new Date().toISOString().split('T')[0],
          metadata: { category: newCategory }
        });
      }
    } catch (err) {
      console.error('Error adding evidence:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleHighlight = async (id: string, currentStatus: boolean) => {
    await supabase
      .from('evidence')
      .update({ is_highlight: !currentStatus })
      .eq('id', id);
    
    loadEvidence();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this evidence?')) return;
    
    await supabase
      .from('evidence')
      .delete()
      .eq('id', id);
    
    loadEvidence();
  };

  const filteredEvidence = selectedCategory === 'all' 
    ? evidence 
    : evidence.filter(e => e.category === selectedCategory);

  const highlightCount = evidence.filter(e => e.is_highlight).length;
  const categoryCounts = Object.keys(CATEGORY_CONFIG).reduce((acc, cat) => {
    acc[cat] = evidence.filter(e => e.category === cat).length;
    return acc;
  }, {} as Record<string, number>);

  const handleClose = () => {
    if (embedded && onClose) {
      onClose();
    } else {
      navigate('/chat');
    }
  };

  const wrapperClass = embedded
    ? "flex-1 overflow-y-auto"
    : "min-h-screen bg-gradient-to-br from-pink-100/40 via-purple-50/40 to-cyan-50/40";

  return (
    <div className={wrapperClass}>
      <ToolHeader
        title="Evidence Bank"
        onBack={handleClose}
        backLabel="Chat"
        rightAction={
          <button
            onClick={() => setShowAddModal(true)}
            className="p-2 text-purple-600 hover:bg-white/30 rounded-xl transition-all"
          >
            <Plus className="w-5 h-5" />
          </button>
        }
      />

      <div className="max-w-2xl mx-auto px-6">{/* Stats */}
        <div className="bg-white/30 backdrop-blur-md border border-white/50 rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-purple-800">Your Transformation Proof</h2>
            <div className="flex items-center gap-1 text-amber-600">
              <Star className="w-4 h-4 fill-current" />
              <span className="text-sm font-medium">{highlightCount} highlights</span>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-2">
            {(Object.keys(CATEGORY_CONFIG) as EvidenceCategory[]).map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(selectedCategory === cat ? 'all' : cat)}
                className={`p-3 rounded-xl text-center transition-all ${
                  selectedCategory === cat
                    ? 'bg-purple-500 text-white shadow-md'
                    : 'bg-white/40 hover:bg-white/50 border border-white/50'
                }`}
              >
                <span className="text-xl block mb-1">{CATEGORY_CONFIG[cat].emoji}</span>
                <span className={`text-xs font-medium ${selectedCategory === cat ? 'text-white' : 'text-purple-700'}`}>{categoryCounts[cat] || 0}</span>
              </button>
            ))}
          </div>

          {selectedCategory !== 'all' && (
            <p className="text-sm text-purple-600 mt-3 text-center">
              {CATEGORY_CONFIG[selectedCategory].description}
            </p>
          )}
        </div>

        {/* Evidence List */}
        {loading ? (
          <div className="text-center py-12">
            <Gem className="w-8 h-8 mx-auto mb-2 text-purple-400 animate-pulse" />
            <p className="text-purple-500">Loading evidence...</p>
          </div>
        ) : filteredEvidence.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 border border-white/60 flex items-center justify-center">
              <Gem className="w-8 h-8 text-purple-500" />
            </div>
            <h3 className="text-lg font-semibold text-purple-800 mb-2">
              No evidence {selectedCategory !== 'all' ? 'in this category ' : ''}yet
            </h3>
            <p className="text-purple-500 text-sm max-w-sm mx-auto mb-6 leading-relaxed">
              Evidence is proof your transformation is working — synchronicities, wins, moments you showed up as your future self, or manifestations appearing in reality.
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              Add your first piece of evidence
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEvidence.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white/30 backdrop-blur-md border rounded-xl p-5 hover:bg-white/40 transition-all ${
                  item.is_highlight ? 'border-amber-300 shadow-lg shadow-amber-500/10' : 'border-white/50'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/50 border border-white/60 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">{CATEGORY_CONFIG[item.category].emoji}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-purple-800 font-medium">{item.description}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-purple-500">
                        {new Date(item.created_at).toLocaleDateString()}
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-white/40 border border-white/50 text-purple-600">
                        {CATEGORY_CONFIG[item.category].label}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleToggleHighlight(item.id, item.is_highlight)}
                      className={`p-1.5 rounded-full transition-colors ${
                        item.is_highlight
                          ? 'text-amber-600 bg-amber-100 border border-amber-200'
                          : 'text-purple-400 hover:text-amber-600 hover:bg-amber-50'
                      }`}
                    >
                      <Star className={`w-4 h-4 ${item.is_highlight ? 'fill-current' : ''}`} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1.5 rounded-full text-purple-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add Evidence Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-purple-900">Log Evidence</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Category Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What kind of evidence is this?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(CATEGORY_CONFIG) as EvidenceCategory[]).map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setNewCategory(cat)}
                      className={`p-3 rounded-xl text-left transition-all ${
                        newCategory === cat 
                          ? `bg-gradient-to-br ${CATEGORY_CONFIG[cat].color} text-white` 
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span className="text-lg mr-2">{CATEGORY_CONFIG[cat].emoji}</span>
                      <span className="text-sm font-medium">{CATEGORY_CONFIG[cat].label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What happened?
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Describe your evidence..."
                  rows={3}
                  className="w-full p-4 rounded-xl border border-gray-200 focus:border-purple-400 focus:ring-0 outline-none transition-colors resize-none"
                />
              </div>

              {/* Highlight Toggle */}
              <label className="flex items-center gap-3 mb-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newIsHighlight}
                  onChange={(e) => setNewIsHighlight(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                />
                <span className="text-gray-700">Mark as highlight ⭐</span>
              </label>

              {/* Submit */}
              <button
                onClick={handleAddEvidence}
                disabled={!newDescription.trim() || saving}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Add Evidence'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default EvidenceBank;
